import { FC, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { t } from 'i18next'
import { reportError } from '../core/error/handler'
import { ErrorLevels } from '../core/error/constants'
import { RoutePath } from '../core/routes/routePaths'
import { uploadImage } from '../commons/components/uploader/utils'
import CustomDialog from '../ui/custom-dialog/CustomDialog'
import OfferCategoriesForm from './OfferCategoriesForm'
import { useAppDispatch, useAppSelector } from '../core/redux/hooks'
import { getAllAgencies } from '../agencies/slices'
import {
  ReorderList,
  errorDisplay,
  getErrorMessage,
  handleReorderSave,
  validationErrorsDisplay,
  getItemKey,
  isValid
} from '../commons/utils/utils'
import {
  createOfferCategory,
  resetCategoryForm,
  getOfferCategories
} from './slices'
import ReorderUiPositionWrapperCreate from '../ui/reorder-ui-position-wrapper/ReorderUiPositionWrapperCreate'
import { REORDER_KEY_NAME } from '../commons/constants/constants'
import { RoleType } from '../core/routes/constants'

const OfferCategoriesCreate: FC<{}> = () => {
  const navigate = useNavigate()
  const [formAgencyID, setFormAgencyID] = useState('')
  const [categoryName, setCategoryName] = useState('')
  const [icon, setIcon] = useState<File | undefined>()
  const [webUiPosition, setWebUiPosition] = useState('')
  const { userID, agencyID, tenantID, userType, isAgencyEnabled } =
    useAppSelector((state) => state.auth)
  const [errorMessage, setErrorMessage] = useState('')
  const [openConfirmLeaving, setOpenConfirmLeaving] = useState(false)
  const [openConfirmCreateCategory, setOpenConfirmCreateCategory] =
    useState(false)
  const dispatch = useAppDispatch()
  const { loading, isFulfilled, errors, offerCategories, OfferCategoryID } =
    useAppSelector((state) => ({
      loading: state.offerCategories.loading,
      isFulfilled: state.offerCategories.isFulfilled,
      errors: state.offerCategories.errors,
      offerCategories: state.offerCategories.offerCategories,
      OfferCategoryID:
        state.offerCategories.offerCategorySelected.OfferCategoryID
    }))
  const [isUIReorderOpen, setOpenUIreorder] = useState<boolean>(false)
  const [listWithNewOrder, setListWithNewOrder] = useState<ReorderList>([])
  const [updatedUIs, setUpdatedUIs] = useState<{
    [key: number]: string | number | undefined
  }>({})
  const [resetUIs, setResetUIs] = useState<boolean>(false)
  const [isNameUpdated, setIsCategoryNameUpdated] = useState<boolean>(false)

  useEffect(() => {
    if (userType !== RoleType.AGENCY && isAgencyEnabled) {
      void dispatch(getAllAgencies({ tenantID }))
    }
  }, [tenantID, dispatch, isAgencyEnabled, userType])

  const handleClose = (): void => {
    setOpenConfirmLeaving(false)
    setOpenConfirmCreateCategory(false)
  }

  const createOfferCategorySubmission = async (): Promise<void> => {
    try {
      const upload = await uploadImage(userID, icon)
      void dispatch(
        createOfferCategory({
          CategoryName: categoryName,
          WebUiPosition: webUiPosition,
          IconImageID: upload.imageID,
          TenantID: tenantID,
          AgencyID: agencyID ?? formAgencyID,
          UpdatedUIs: updatedUIs
        })
      )
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error))
      reportError(error, ErrorLevels.Error)
    } finally {
      handleClose()
    }
  }

  useEffect(() => {
    if (isFulfilled && OfferCategoryID != null) {
      dispatch(resetCategoryForm())
      navigate(`${RoutePath.OfferCategories}/${OfferCategoryID}`)
    }
  }, [isFulfilled, navigate, dispatch, OfferCategoryID])

  useEffect(() => {
    if (agencyID != null || formAgencyID !== '' || !isAgencyEnabled) {
      void dispatch(
        getOfferCategories({
          tenantID,
          userType,
          agencyID: agencyID ?? formAgencyID
        })
      )
    }
  }, [dispatch, agencyID, formAgencyID, tenantID, userType, isAgencyEnabled])

  useEffect(() => {
    if (isNameUpdated) {
      const updatedList = listWithNewOrder.map((category) => {
        const categoryIDKey = getItemKey(
          category,
          REORDER_KEY_NAME.CategoryIDKeyWord
        )
        if (categoryIDKey != null) {
          if (category[categoryIDKey as keyof typeof category] == null) {
            return { ...category, CategoryName: categoryName }
          }
        }
        return category
      })
      setListWithNewOrder(updatedList)
      setIsCategoryNameUpdated(false)
    }
  }, [isNameUpdated, categoryName, listWithNewOrder])

  useEffect(() => {
    if (resetUIs) {
      setWebUiPosition('')
      setUpdatedUIs({})
      setListWithNewOrder([])
      setResetUIs(false)
    }
  }, [resetUIs])

  const renderConfirmCreateCategoryDialog = (
    <CustomDialog
      title={t`ConfirmOfferCategoryCreation`}
      open={openConfirmCreateCategory}
      cancelCallback={handleClose}
      okCallback={createOfferCategorySubmission}
      mainMessage={t('OfferCategoryCreateDialogMessage', { categoryName })}
      okText={t`Create`}
      cancelText={t`Cancel`}
    />
  )
  const onReorderSave = (list: ReorderList): void => {
    handleReorderSave(
      categoryName,
      list,
      setWebUiPosition,
      setOpenUIreorder,
      setUpdatedUIs,
      setListWithNewOrder,
      false,
      false,
      false,
      false
    )
  }
  useEffect(() => {
    dispatch(resetCategoryForm())
  }, [dispatch])

  const renderConfirmLeavingDialog = (
    <CustomDialog
      title={t`DiscardOfferCategoryCreation`}
      open={openConfirmLeaving}
      cancelCallback={handleClose}
      okCallback={() => navigate(RoutePath.OfferCategories)}
      mainMessage={t`DiscardMessage`}
      okText={t`Leave`}
      cancelText={t`Cancel`}
    />
  )

  const setValueMap = {
    agencyID: setFormAgencyID,
    categoryName: setCategoryName,
    icon: setIcon,
    webUiPosition: setWebUiPosition,
    url: setIcon
  }

  if (loading) {
    return (
      <Box className='centered-box'>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <OfferCategoriesForm
        edit={false}
        onSubmit={() => setOpenConfirmCreateCategory(true)}
        setValueMap={setValueMap}
        properties={{
          agencyID: formAgencyID,
          categoryName,
          icon,
          webUiPosition
        }}
        onDiscard={() => setOpenConfirmLeaving(true)}
        isLeavingDialogOpen={openConfirmLeaving}
        onUiReorderClick={() => setOpenUIreorder(true)}
        isReorderDisabled={
          (formAgencyID !== '' || agencyID != null || !isAgencyEnabled) &&
          isValid(categoryName)
        }
        resetUIs={() => setResetUIs(true)}
        setIsCategoryNameUpdated={() => setIsCategoryNameUpdated(true)}
      />
      {errorDisplay(errorMessage)}
      {isUIReorderOpen && (
        <ReorderUiPositionWrapperCreate
          isUIReorderOpen={isUIReorderOpen}
          handleClose={() => setOpenUIreorder(false)}
          name={categoryName}
          onReorderSave={onReorderSave}
          currentList={offerCategories}
          listWithNewOrder={listWithNewOrder}
        />
      )}
      {renderConfirmCreateCategoryDialog}
      {renderConfirmLeavingDialog}
      {typeof errors === 'string'
        ? errorDisplay(errors)
        : validationErrorsDisplay(errors)}
    </Box>
  )
}

export default OfferCategoriesCreate
